import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Download,
  Type,
  Sun,
  Contrast,
  Palette,
  RotateCcw,
  Plus,
  Trash2,
  Move,
  Check,
  X,
  Image as ImageIcon,
  Loader2,
  Wand2,
  ScanText,
  Replace,
  Undo2,
  History,
  Save
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FONTS = [
  { value: "Impact", label: "Impact (YouTube Classic)" },
  { value: "Arial Black", label: "Arial Black" },
  { value: "Bebas Neue", label: "Bebas Neue" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Figtree", label: "Figtree" },
  { value: "Glacial Indifference", label: "Glacial Indifference" },
  { value: "Poppins", label: "Poppins" },
  { value: "Inter", label: "Inter" },
  { value: "Oswald", label: "Oswald" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
];

const TEXT_COLORS = [
  { value: "#FFFFFF", label: "Weiß" },
  { value: "#FFFF00", label: "Gelb" },
  { value: "#FF0000", label: "Rot" },
  { value: "#00FF00", label: "Grün" },
  { value: "#00FFFF", label: "Cyan" },
  { value: "#FF00FF", label: "Magenta" },
  { value: "#000000", label: "Schwarz" },
];

const ThumbnailEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const thumbnailId = searchParams.get("id");
  
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [originalImage, setOriginalImage] = useState(null);
  const [thumbnailData, setThumbnailData] = useState(null);
  
  // Image adjustments
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  
  // Text overlays
  const [textOverlays, setTextOverlays] = useState([]);
  const [activeTextIndex, setActiveTextIndex] = useState(null);
  const [newText, setNewText] = useState("");
  const [newTextFont, setNewTextFont] = useState("Impact");
  const [newTextColor, setNewTextColor] = useState("#FFFFFF");
  const [newTextSize, setNewTextSize] = useState(48);
  const [newTextStroke, setNewTextStroke] = useState(true);
  const [newTextLetterSpacing, setNewTextLetterSpacing] = useState(0);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // AI Text Editing
  const [detectedTexts, setDetectedTexts] = useState([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [selectedDetectedText, setSelectedDetectedText] = useState(null);
  const [replacementText, setReplacementText] = useState("");
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  
  // Undo History - stores previous image states
  const [imageHistory, setImageHistory] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const MAX_HISTORY = 10; // Maximum number of undo steps

  // Load thumbnail
  useEffect(() => {
    const loadThumbnail = async () => {
      if (!thumbnailId) {
        toast.error("Keine Thumbnail-ID angegeben");
        navigate("/jobs");
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/thumbnails/${thumbnailId}`);
        setThumbnailData(response.data);
        
        // Load image
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setOriginalImage(img);
          setIsLoading(false);
        };
        img.onerror = () => {
          toast.error("Fehler beim Laden des Bildes");
          setIsLoading(false);
        };
        
        // Use base64 data if available
        if (response.data.image_data) {
          const base64 = response.data.image_data.startsWith('data:') 
            ? response.data.image_data 
            : `data:image/png;base64,${response.data.image_data}`;
          img.src = base64;
        } else {
          toast.error("Keine Bilddaten verfügbar");
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error loading thumbnail:", err);
        toast.error("Fehler beim Laden des Thumbnails");
        setIsLoading(false);
      }
    };
    
    loadThumbnail();
  }, [thumbnailId, navigate]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    if (!canvasRef.current || !originalImage) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Set canvas size to 16:9 YouTube format
    const targetWidth = 1280;
    const targetHeight = 720;
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply filters
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    
    // Draw image scaled to fit
    const scale = Math.max(targetWidth / originalImage.width, targetHeight / originalImage.height);
    const x = (targetWidth - originalImage.width * scale) / 2;
    const y = (targetHeight - originalImage.height * scale) / 2;
    ctx.drawImage(originalImage, x, y, originalImage.width * scale, originalImage.height * scale);
    
    // Reset filter for text
    ctx.filter = "none";
    
    // Draw text overlays
    textOverlays.forEach((overlay, index) => {
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      ctx.fillStyle = overlay.color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.letterSpacing = `${overlay.letterSpacing || 0}px`;
      
      // Draw stroke if enabled
      if (overlay.stroke) {
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = overlay.size / 10;
        ctx.lineJoin = "round";
        ctx.strokeText(overlay.text, overlay.x, overlay.y);
      }
      
      // Draw text
      ctx.fillText(overlay.text, overlay.x, overlay.y);
      
      // Reset letterSpacing
      ctx.letterSpacing = "0px";
      
      // Draw selection box if active
      if (index === activeTextIndex) {
        const metrics = ctx.measureText(overlay.text);
        const textWidth = metrics.width;
        const textHeight = overlay.size;
        
        ctx.strokeStyle = "#0EA5A4";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(
          overlay.x - textWidth / 2 - 10,
          overlay.y - textHeight / 2 - 5,
          textWidth + 20,
          textHeight + 10
        );
        ctx.setLineDash([]);
      }
    });
  }, [originalImage, brightness, contrast, saturation, textOverlays, activeTextIndex]);

  // Re-render when dependencies change
  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Add text overlay
  const addTextOverlay = () => {
    if (!newText.trim()) {
      toast.error("Bitte Text eingeben");
      return;
    }
    
    const newOverlay = {
      text: newText,
      font: newTextFont,
      color: newTextColor,
      size: newTextSize,
      stroke: newTextStroke,
      letterSpacing: newTextLetterSpacing,
      x: 640,
      y: 360,
    };
    
    setTextOverlays([...textOverlays, newOverlay]);
    setActiveTextIndex(textOverlays.length);
    setNewText("");
    toast.success("Text hinzugefügt");
  };

  // Remove text overlay
  const removeTextOverlay = (index) => {
    setTextOverlays(textOverlays.filter((_, i) => i !== index));
    setActiveTextIndex(null);
    toast.success("Text entfernt");
  };

  // Handle canvas mouse events for dragging
  const handleCanvasMouseDown = (e) => {
    if (activeTextIndex === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const overlay = textOverlays[activeTextIndex];
    if (overlay) {
      const ctx = canvas.getContext("2d");
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      const metrics = ctx.measureText(overlay.text);
      const textWidth = metrics.width;
      const textHeight = overlay.size;
      
      // Check if click is on active text
      if (
        x >= overlay.x - textWidth / 2 - 10 &&
        x <= overlay.x + textWidth / 2 + 10 &&
        y >= overlay.y - textHeight / 2 - 5 &&
        y <= overlay.y + textHeight / 2 + 5
      ) {
        setIsDragging(true);
        setDragOffset({ x: x - overlay.x, y: y - overlay.y });
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDragging || activeTextIndex === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setTextOverlays(textOverlays.map((overlay, index) => {
      if (index === activeTextIndex) {
        return {
          ...overlay,
          x: Math.max(50, Math.min(1230, x - dragOffset.x)),
          y: Math.max(50, Math.min(670, y - dragOffset.y)),
        };
      }
      return overlay;
    }));
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };

  // Click on canvas to select text
  const handleCanvasClick = (e) => {
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Check if click is on any text
    let foundIndex = null;
    const ctx = canvas.getContext("2d");
    
    textOverlays.forEach((overlay, index) => {
      ctx.font = `bold ${overlay.size}px ${overlay.font}`;
      const metrics = ctx.measureText(overlay.text);
      const textWidth = metrics.width;
      const textHeight = overlay.size;
      
      if (
        x >= overlay.x - textWidth / 2 - 10 &&
        x <= overlay.x + textWidth / 2 + 10 &&
        y >= overlay.y - textHeight / 2 - 5 &&
        y <= overlay.y + textHeight / 2 + 5
      ) {
        foundIndex = index;
      }
    });
    
    setActiveTextIndex(foundIndex);
  };

  // Reset adjustments
  const resetAdjustments = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    toast.success("Anpassungen zurückgesetzt");
  };

  // Create 2K export from current canvas state
  const create2KExport = () => {
    if (!originalImage) return null;
    const exportCanvas = document.createElement("canvas");
    const exportCtx = exportCanvas.getContext("2d");
    const exportWidth = 2560;
    const exportHeight = 1440;
    exportCanvas.width = exportWidth;
    exportCanvas.height = exportHeight;
    
    exportCtx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    const scale = Math.max(exportWidth / originalImage.width, exportHeight / originalImage.height);
    const x = (exportWidth - originalImage.width * scale) / 2;
    const y = (exportHeight - originalImage.height * scale) / 2;
    exportCtx.drawImage(originalImage, x, y, originalImage.width * scale, originalImage.height * scale);
    exportCtx.filter = "none";
    
    const scaleRatio = exportWidth / 1280;
    textOverlays.forEach((overlay) => {
      const scaledSize = overlay.size * scaleRatio;
      exportCtx.font = `bold ${scaledSize}px ${overlay.font}`;
      exportCtx.fillStyle = overlay.color;
      exportCtx.textAlign = "center";
      exportCtx.textBaseline = "middle";
      exportCtx.letterSpacing = `${(overlay.letterSpacing || 0) * scaleRatio}px`;
      const scaledX = overlay.x * scaleRatio;
      const scaledY = overlay.y * scaleRatio;
      if (overlay.stroke) {
        exportCtx.strokeStyle = "#000000";
        exportCtx.lineWidth = scaledSize / 10;
        exportCtx.lineJoin = "round";
        exportCtx.strokeText(overlay.text, scaledX, scaledY);
      }
      exportCtx.fillText(overlay.text, scaledX, scaledY);
      exportCtx.letterSpacing = "0px";
    });
    
    return exportCanvas;
  };

  // Save current image to database in 2K (manual action)
  const saveImageToDb = async () => {
    if (!thumbnailId || !originalImage) return;
    setIsSaving(true);
    try {
      const exportCanvas = create2KExport();
      if (!exportCanvas) throw new Error("Export failed");
      console.log(`Saving at ${exportCanvas.width}x${exportCanvas.height}`);
      const base64Data = exportCanvas.toDataURL("image/png").split(",")[1];
      await axios.put(`${BACKEND_URL}/api/thumbnails/${thumbnailId}/update-image`, {
        image_data: base64Data
      });
      setHasUnsavedChanges(false);
      toast.success(`Bild gespeichert (${exportCanvas.width}x${exportCanvas.height})`);
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Fehler beim Speichern: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  // Download image in 2K resolution (2560x1440)
  const downloadImage = (format = "png") => {
    if (!originalImage) return;
    
    const prevActive = activeTextIndex;
    setActiveTextIndex(null);
    
    setTimeout(() => {
      const exportCanvas = create2KExport();
      if (!exportCanvas) return;
      
      const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
      const quality = format === "jpg" ? 0.95 : undefined;
      
      const link = document.createElement("a");
      link.download = `thumbnail-${thumbnailId}-2K.${format}`;
      link.href = exportCanvas.toDataURL(mimeType, quality);
      link.click();
      
      setActiveTextIndex(prevActive);
      toast.success(`Thumbnail ${exportCanvas.width}x${exportCanvas.height} als ${format.toUpperCase()} heruntergeladen`);
    }, 100);
  };

  // AI Text Detection
  const detectTextInImage = async () => {
    if (!originalImage) return;
    
    setIsDetecting(true);
    try {
      // Send original image at FULL resolution, not the display canvas
      const fullResCanvas = document.createElement("canvas");
      fullResCanvas.width = originalImage.width;
      fullResCanvas.height = originalImage.height;
      const fullResCtx = fullResCanvas.getContext("2d");
      fullResCtx.drawImage(originalImage, 0, 0);
      const imageData = fullResCanvas.toDataURL("image/png").split(",")[1];
      
      const response = await axios.post(`${BACKEND_URL}/api/editor/detect-text`, {
        image_data: imageData
      });
      
      if (response.data.texts && response.data.texts.length > 0) {
        setDetectedTexts(response.data.texts);
        toast.success(`${response.data.texts.length} Text(e) erkannt`);
      } else {
        setDetectedTexts([]);
        toast.info("Kein Text im Bild erkannt");
      }
    } catch (err) {
      console.error("Text detection error:", err);
      toast.error("Fehler bei der Texterkennung");
    } finally {
      setIsDetecting(false);
    }
  };

  // AI Text Replacement using Nano Banana Pro
  const replaceTextInImage = async () => {
    if (!selectedDetectedText) {
      toast.error("Bitte Text auswählen");
      return;
    }
    
    const isDeleting = !replacementText.trim();
    const newText = isDeleting ? " " : replacementText;
    
    setIsReplacing(true);
    try {
      // Send original image at FULL resolution, not the display canvas
      const fullResCanvas = document.createElement("canvas");
      fullResCanvas.width = originalImage.width;
      fullResCanvas.height = originalImage.height;
      const fullResCtx = fullResCanvas.getContext("2d");
      fullResCtx.drawImage(originalImage, 0, 0);
      const imageData = fullResCanvas.toDataURL("image/png").split(",")[1];
      
      // Save current image to history BEFORE making changes
      if (originalImage) {
        setImageHistory(prev => {
          const newHistory = [...prev, originalImage];
          // Limit history size
          if (newHistory.length > MAX_HISTORY) {
            return newHistory.slice(-MAX_HISTORY);
          }
          return newHistory;
        });
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/editor/replace-text`, {
        image_data: imageData,
        original_text: selectedDetectedText.text,
        new_text: newText
      });
      
      if (response.data.success && response.data.image_data) {
        // Load the edited image
        const editedImg = new Image();
        editedImg.crossOrigin = "anonymous";
        editedImg.onload = () => {
          // Update state first
          setOriginalImage(editedImg);
          setShowReplaceDialog(false);
          setSelectedDetectedText(null);
          setReplacementText("");
          setDetectedTexts([]); // Clear detected texts, user can re-detect
          
          // Force canvas redraw with new image
          if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            const targetWidth = 1280;
            const targetHeight = 720;
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
            const scale = Math.max(targetWidth / editedImg.width, targetHeight / editedImg.height);
            const x = (targetWidth - editedImg.width * scale) / 2;
            const y = (targetHeight - editedImg.height * scale) / 2;
            ctx.drawImage(editedImg, x, y, editedImg.width * scale, editedImg.height * scale);
            ctx.filter = "none";
          }
          
          // Mark as unsaved — user must manually save
          setHasUnsavedChanges(true);
          
          toast.success(`Text "${selectedDetectedText.text}" durch "${replacementText}" ersetzt!`);
        };
        editedImg.onerror = (e) => {
          console.error("Image load error:", e);
          // Remove the last history entry since the edit failed
          setImageHistory(prev => prev.slice(0, -1));
          toast.error("Fehler beim Laden des bearbeiteten Bildes");
        };
        editedImg.src = `data:image/png;base64,${response.data.image_data}`;
      } else {
        // Remove the last history entry since the edit failed
        setImageHistory(prev => prev.slice(0, -1));
        // Show error from backend
        toast.error(response.data.error || "KI konnte den Text nicht ersetzen");
      }
    } catch (err) {
      console.error("Text replacement error:", err);
      // Remove the last history entry since the edit failed
      setImageHistory(prev => prev.slice(0, -1));
      toast.error("Fehler bei der KI-Textbearbeitung: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsReplacing(false);
    }
  };

  // Undo - restore previous image version
  const undoLastEdit = () => {
    if (imageHistory.length === 0) {
      toast.info("Keine vorherige Version verfügbar");
      return;
    }
    
    const previousImage = imageHistory[imageHistory.length - 1];
    setImageHistory(prev => prev.slice(0, -1));
    setOriginalImage(previousImage);
    setDetectedTexts([]);
    setHasUnsavedChanges(true);
    
    toast.success("Letzte Änderung rückgängig gemacht");
  };

  // Open replace dialog for a detected text
  const openReplaceDialog = (textItem) => {
    setSelectedDetectedText(textItem);
    setReplacementText(textItem.text);
    setShowReplaceDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="thumbnail-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => {
            const fromJob = searchParams.get("fromJob");
            if (fromJob) {
              navigate(`/jobs?openJob=${fromJob}`);
            } else {
              navigate(-1);
            }
          }} data-testid="thumbnail-editor-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Thumbnail bearbeiten</h1>
            <p className="text-muted-foreground text-sm">
              {thumbnailData?.video_title || "Thumbnail Editor"}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {/* Unsaved changes indicator + Save button */}
          {hasUnsavedChanges && (
            <Button 
              onClick={saveImageToDb}
              disabled={isSaving}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="save-button"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Speichern
            </Button>
          )}
          {/* Undo Button - only show when history exists */}
          {imageHistory.length > 0 && (
            <Button 
              variant="outline" 
              onClick={undoLastEdit}
              className="gap-2"
              data-testid="undo-button"
            >
              <Undo2 className="w-4 h-4" />
              Rückgängig
              <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
                {imageHistory.length}
              </span>
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadImage("png")}>
            <Download className="w-4 h-4 mr-2" />
            PNG
          </Button>
          <Button onClick={() => downloadImage("jpg")}>
            <Download className="w-4 h-4 mr-2" />
            JPG
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Canvas */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-4">
              <div className="relative bg-muted/30 rounded-lg overflow-hidden">
                <canvas
                  ref={canvasRef}
                  className="w-full cursor-move"
                  style={{ aspectRatio: "16/9" }}
                  onClick={handleCanvasClick}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Klicke auf Text zum Auswählen • Ziehe zum Verschieben
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <Tabs defaultValue="ai">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ai">
                <Wand2 className="w-4 h-4 mr-2" />
                KI
              </TabsTrigger>
              <TabsTrigger value="text">
                <Type className="w-4 h-4 mr-2" />
                Text
              </TabsTrigger>
              <TabsTrigger value="adjust">
                <Sun className="w-4 h-4 mr-2" />
                Anpassen
              </TabsTrigger>
            </TabsList>

            {/* AI Tab */}
            <TabsContent value="ai" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    KI Text-Bearbeitung
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Lasse die KI den Text im Bild erkennen und ersetzen.
                  </p>
                  
                  <Button 
                    onClick={detectTextInImage} 
                    disabled={isDetecting}
                    className="w-full"
                  >
                    {isDetecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Erkenne Text...
                      </>
                    ) : (
                      <>
                        <ScanText className="w-4 h-4 mr-2" />
                        Text erkennen
                      </>
                    )}
                  </Button>

                  {/* Detected Texts */}
                  {detectedTexts.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm">Erkannte Texte:</Label>
                      {detectedTexts.map((textItem, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => openReplaceDialog(textItem)}
                          data-testid={`detected-text-${index}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{textItem.text}</p>
                            <p className="text-xs text-muted-foreground">
                              {textItem.position?.vertical} {textItem.position?.horizontal} • {textItem.size} • {textItem.color}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              title="Text löschen"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDetectedText(textItem);
                                setReplacementText(" ");
                                setShowReplaceDialog(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              title="Text ersetzen"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReplaceDialog(textItem);
                              }}
                            >
                              <Replace className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {detectedTexts.length === 0 && !isDetecting && (
                    <p className="text-xs text-center text-muted-foreground py-4">
                      Klicke auf "Text erkennen" um Texte im Bild zu finden
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Text Tab */}
            <TabsContent value="text" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Neuer Text</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="new-text">Text</Label>
                    <Input
                      id="new-text"
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      placeholder="Text eingeben..."
                      data-testid="text-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Schriftart</Label>
                      <Select value={newTextFont} onValueChange={setNewTextFont}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONTS.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Farbe</Label>
                      <Select value={newTextColor} onValueChange={setNewTextColor}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEXT_COLORS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded border"
                                  style={{ backgroundColor: color.value }}
                                />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Größe: {newTextSize}px</Label>
                    <Slider
                      value={[newTextSize]}
                      onValueChange={([v]) => setNewTextSize(v)}
                      min={24}
                      max={120}
                      step={2}
                    />
                  </div>
                  
                  <div>
                    <Label>Zeichenabstand: {newTextLetterSpacing}px</Label>
                    <Slider
                      value={[newTextLetterSpacing]}
                      onValueChange={([v]) => setNewTextLetterSpacing(v)}
                      min={-2}
                      max={20}
                      step={1}
                    />
                  </div>
                  
                  <Button onClick={addTextOverlay} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Text hinzufügen
                  </Button>
                </CardContent>
              </Card>

              {/* Text list */}
              {textOverlays.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Textelemente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {textOverlays.map((overlay, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-colors ${
                          activeTextIndex === index ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                        }`}
                        onClick={() => setActiveTextIndex(index)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: overlay.color }}
                          />
                          <span className="truncate text-sm">{overlay.text}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTextOverlay(index);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Adjust Tab */}
            <TabsContent value="adjust" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    Bildanpassungen
                    <Button variant="ghost" size="sm" onClick={resetAdjustments}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Helligkeit: {brightness}%
                    </Label>
                    <Slider
                      value={[brightness]}
                      onValueChange={([v]) => setBrightness(v)}
                      min={50}
                      max={150}
                      step={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <Contrast className="w-4 h-4" />
                      Kontrast: {contrast}%
                    </Label>
                    <Slider
                      value={[contrast]}
                      onValueChange={([v]) => setContrast(v)}
                      min={50}
                      max={150}
                      step={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Sättigung: {saturation}%
                    </Label>
                    <Slider
                      value={[saturation]}
                      onValueChange={([v]) => setSaturation(v)}
                      min={0}
                      max={200}
                      step={1}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Text Replace Dialog */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{!replacementText.trim() ? 'Text aus Bild löschen' : 'Text mit KI ersetzen'}</DialogTitle>
            <DialogDescription>
              {!replacementText.trim() 
                ? 'Nano Banana Pro entfernt den erkannten Text aus dem Bild und füllt den Hintergrund passend auf.'
                : 'Nano Banana Pro ersetzt den erkannten Text direkt im Bild - mit gleichem Stil und Position.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-muted-foreground">Erkannter Text:</Label>
              <p className="font-medium mt-1 p-2 bg-muted rounded">
                {selectedDetectedText?.text}
              </p>
            </div>
            
            <div>
              <Label htmlFor="replacement-text">Neuer Text (leer lassen zum Löschen):</Label>
              <Input
                id="replacement-text"
                value={replacementText}
                onChange={(e) => setReplacementText(e.target.value)}
                placeholder="Leer lassen um Text zu entfernen..."
                className="mt-1"
                data-testid="replacement-text-input"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowReplaceDialog(false)} disabled={isReplacing}>
              Abbrechen
            </Button>
            <Button onClick={replaceTextInImage} disabled={isReplacing}>
              {isReplacing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {!replacementText.trim() ? 'Lösche Text...' : 'Ersetze Text...'}
                </>
              ) : !replacementText.trim() ? (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Text löschen
                </>
              ) : (
                <>
                  <Replace className="w-4 h-4 mr-2" />
                  Text ersetzen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThumbnailEditor;
